import { flat, group, mapValues, sort } from 'radash'
import { format, getUnixTime, isAfter, parse } from 'date-fns'
import { transformTransportation } from './transportation'
import type { bookingService } from '@/services/booking'
import type { myBookingService } from '@/services'
import type { GetBookingDetailResponse } from '@/types/booking'
import type { InfiniteData } from '@tanstack/react-query'

export const transformBooking = (
  booking: GetBookingDetailResponse['data']['booking'],
): GetBookingDetailResponse['data']['booking'] => {
  const busWithTypes =
    booking.adhoc_booking_booked_transportations?.filter(
      (b) => !!b.adhoc_transportation_type,
    ) ?? []
  const transportations = busWithTypes.length
    ? flat(
        Object.values(
          mapValues(
            group(busWithTypes, (item) => item.adhoc_transportation_type.name),
            (val, key) =>
              val?.map((v, idx) => ({
                ...v,
                _label: [key, ...(val.length > 1 ? [`#${idx + 1}`] : [])].join(
                  ' ',
                ),
                _created_at_unix: getUnixTime(
                  parse(
                    v.created_at,
                    "yyyy-MM-dd HH:mm:ss 'GMT'xx",
                    new Date(),
                  ),
                ),
              })) ?? [],
          ),
        ),
      )
    : []

  const adhoc_booking_booked_transportations = sort(
    transportations,
    (t) => t._created_at_unix,
  ).map((transportation) => transformTransportation(transportation))

  const now = new Date()

  const _label_payment_due_date = booking.payment_due_date
    ? format(
        parse(
          booking.payment_due_date,
          "yyyy-MM-dd HH:mm:ss 'GMT'xx",
          new Date(),
        ),
        'dd MMMM yyyy, hh:mm a',
      )
    : '-'
  const _label_booking_created_at = booking.created_at
    ? format(
        parse(booking.created_at, "yyyy-MM-dd HH:mm:ss 'GMT'xx", new Date()),
        'dd MMMM yyyy, hh:mm a',
      )
    : '-'
  const _label_cancellation_date = booking.cancellation_date
    ? format(
        parse(
          booking.cancellation_date,
          "yyyy-MM-dd HH:mm:ss 'GMT'xx",
          new Date(),
        ),
        'dd MMMM yyyy, hh:mm a',
      )
    : '-'
  const _label_payment_received_at = booking.payment_received_at
    ? format(
        parse(
          booking.payment_received_at,
          "yyyy-MM-dd HH:mm:ss 'GMT'xx",
          new Date(),
        ),
        'dd MMMM yyyy, hh:mm a',
      )
    : '-'

  const _is_payment_overdue = booking.payment_due_date
    ? isAfter(
        now,
        parse(
          booking.payment_due_date,
          "yyyy-MM-dd HH:mm:ss 'GMT'xx",
          new Date(),
        ),
      )
    : false

  return {
    ...booking,
    // @ts-expect-error
    ...(booking.parent ? { parent: transformBooking(booking.parent) } : {}),
    adhoc_booking_booked_transportations,
    _label_payment_due_date,
    _label_booking_created_at,
    _label_cancellation_date,
    _label_payment_received_at,
    _is_payment_overdue,
    _previous_payment_method: booking.payment_method,
  }
}

export const selectionBookingDetail = (
  res: Awaited<ReturnType<typeof bookingService.detail>>,
) => {
  return {
    ...res,
    data: {
      ...res.data,
      booking: transformBooking(res.data.booking),
    },
  }
}

export const selectionBookingList = (
  res: InfiniteData<Awaited<ReturnType<typeof myBookingService.allBookings>>>,
) => {
  return {
    ...res,
    pages: res.pages.map((page) => ({
      ...page,
      data: {
        ...page.data,
        bookings: page.data.bookings.map((booking) =>
          transformBooking(booking),
        ),
      },
    })),
  }
}
